import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function WorkflowBatchApprovalPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [execution, setExecution] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    fetch('/api/workflows/executions/' + runId, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setExecution(d.data || d.execution || d);
        // Extract drafts from step outputs
        const results = d.data?.context?.results || d.execution?.context?.results || [];
        setDrafts(results.map((r, i) => ({
          id: i,
          recipient: r.item || r.output?.to || 'Unknown',
          subject: r.output?.subject || 'No subject',
          body: r.output?.body_html || r.output?.draft || '',
          approved: null,
          edited: false,
        })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [runId]);

  const updateDraft = (i, field, value) => {
    setDrafts(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: value, edited: true } : d));
  };

  const approveDraft = (i, decision) => {
    setDrafts(prev => prev.map((d, idx) => idx === i ? { ...d, approved: decision } : d));
  };

  const approveAll = async (decision) => {
    setApproving(true);
    setDrafts(prev => prev.map(d => ({ ...d, approved: decision })));
    try {
      await fetch('/api/workflows/executions/' + runId + '/approve', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, edited_drafts: drafts }),
      });
    } catch {}
    setApproving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Drafts</h1>
          <p className="text-sm text-gray-500 mt-1">{drafts.length} items awaiting your approval</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => approveAll('rejected')} disabled={approving} className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50">Reject All</button>
          <button onClick={() => approveAll('approved')} disabled={approving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">{approving ? 'Approving...' : 'Approve All'}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {drafts.map((draft, i) => (
          <div key={i} className={`rounded-xl border p-4 ${draft.approved === 'approved' ? 'border-green-300 bg-green-50' : draft.approved === 'rejected' ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{typeof draft.recipient === 'string' ? draft.recipient : JSON.stringify(draft.recipient)}</span>
              {draft.approved && <span className={`text-xs px-2 py-0.5 rounded-full ${draft.approved === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{draft.approved}</span>}
            </div>
            <input value={draft.subject} onChange={e => updateDraft(i, 'subject', e.target.value)} className="w-full text-sm font-semibold mb-2 px-2 py-1 border border-gray-200 rounded" />
            <textarea value={draft.body} onChange={e => updateDraft(i, 'body', e.target.value)} rows={4} className="w-full text-sm text-gray-600 px-2 py-1 border border-gray-200 rounded resize-y" />
            <div className="flex gap-2 mt-3">
              <button onClick={() => approveDraft(i, 'rejected')} className="px-3 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50">Reject</button>
              <button onClick={() => approveDraft(i, 'approved')} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">Approve</button>
            </div>
          </div>
        ))}
      </div>

      {drafts.length === 0 && <p className="text-center text-gray-400 py-12">No drafts to review</p>}
    </div>
  );
}
